/**
 * SleepPlanOnboarding
 *
 * Bottom-sheet onboarding flow for the Sleep Plan feature.
 * 5 steps: Introduction, Data Check, Mode Selection, Notifications, Summary.
 * Safe-area aware for mobile.
 */

import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  X,
  ChevronRight,
  ChevronLeft,
  Moon,
  Bell,
  CheckCircle,
  Info,
  Loader2,
  Eye,
  BookOpen,
  Zap,
  Lock,
} from 'lucide-react';
import { useTranslation } from '@parentpath/i18n';
import { usePregnancyStore } from '@/store/pregnancyStore';
import { getBabyName } from '@/lib/babyUtils';
import { getCorrectedAgeWeeks, getWakeWindowRange } from '../lib/wakeWindowCalculator';
import type { UseSleepPlanReturn } from '../hooks/useSleepPlan';

interface SleepPlanOnboardingProps {
  onClose: () => void;
  onActivate: (prematureWeeks: number, mode?: 'observe' | 'learn' | 'active', autoUpgrade?: boolean) => Promise<void>;
  settings: UseSleepPlanReturn['settings'];
}

export function SleepPlanOnboarding({ onClose, onActivate, settings }: SleepPlanOnboardingProps) {
  const { t } = useTranslation('sleepPlan');
  const { lastCompletedPregnancy } = usePregnancyStore();
  const [step, setStep] = useState(1);
  const [isActivating, setIsActivating] = useState(false);
  const [summaryReady, setSummaryReady] = useState(false);

  // Form state
  const [prematureWeeks, setPrematureWeeks] = useState(settings?.prematureWeeks ?? 0);
  const [isPremature, setIsPremature] = useState((settings?.prematureWeeks ?? 0) > 0);
  const [selectedMode, setSelectedMode] = useState<'observe' | 'learn' | 'active'>('observe');
  const [autoUpgrade, setAutoUpgrade] = useState(true);

  const birthDate = lastCompletedPregnancy?.actual_birth_date
    ? new Date(lastCompletedPregnancy.actual_birth_date)
    : null;

  const babyName = getBabyName(lastCompletedPregnancy, 0);

  // Calculate derived values
  const ageWeeks = birthDate ? getCorrectedAgeWeeks(birthDate, 0) : 0;
  const correctedAgeWeeks = birthDate ? getCorrectedAgeWeeks(birthDate, isPremature ? prematureWeeks : 0) : 0;
  const wakeWindow = getWakeWindowRange(correctedAgeWeeks);

  const birthDateFormatted = birthDate
    ? birthDate.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
    : '-';

  // Auto-select mode based on age and feedback data
  const feedbackCount = settings?.totalCyclesWithFeedback ?? 0;
  useEffect(() => {
    if (correctedAgeWeeks >= 14 && feedbackCount >= 10) {
      setSelectedMode('active');
    } else if (correctedAgeWeeks >= 10) {
      setSelectedMode('learn');
    } else {
      setSelectedMode('observe');
    }
  }, [correctedAgeWeeks, feedbackCount]);

  const handleActivate = async () => {
    setIsActivating(true);
    await onActivate(isPremature ? prematureWeeks : 0, selectedMode, autoUpgrade);
    setIsActivating(false);
    onClose();
  };

  const canSelectLearn = correctedAgeWeeks >= 8;
  const canSelectActive = correctedAgeWeeks >= 12 && (settings?.totalCyclesWithFeedback ?? 0) >= 10;

  // Close on Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const totalSteps = 5;

  return createPortal(
    <>
      {/* Backdrop */}
      <div className="bottom-sheet-backdrop" onClick={onClose} />

      {/* Bottom Sheet */}
      <div className="bottom-sheet-container">
        <div className="bottom-sheet-modal" style={{ maxHeight: '85vh' }}>
          {/* Header */}
          <div className="bottom-sheet-header">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-pp-teal/20 rounded-full flex items-center justify-center">
                <Moon className="w-5 h-5 text-pp-teal" />
              </div>
              <div>
                <h2>
                  {step === 1 && t('onboarding.step1Title')}
                  {step === 2 && t('onboarding.step2Title')}
                  {step === 3 && t('onboarding.modeTitle')}
                  {step === 4 && t('onboarding.step3Title')}
                  {step === 5 && t('onboarding.step4Title')}
                </h2>
                <div className="flex gap-1 mt-1">
                  {Array.from({ length: totalSteps }).map((_, i) => (
                    <div
                      key={i}
                      className={`h-1 rounded-full transition-all ${
                        i + 1 <= step ? 'bg-pp-teal w-6' : 'bg-white/20 w-4'
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-[5px] bg-white/20 flex items-center justify-center text-white hover:bg-white/30 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="bottom-sheet-content space-y-4 overflow-y-auto" style={{ maxHeight: '50vh' }}>
            {/* Step 1: Introduction */}
            {step === 1 && (
              <>
                <p className="text-white/80 text-sm leading-relaxed">
                  {t('onboarding.step1Description')}
                </p>
                <div className="glass-effect rounded-[15px] p-4">
                  <div className="flex items-start gap-3">
                    <Info className="w-5 h-5 text-pp-teal flex-shrink-0 mt-0.5" />
                    <p className="text-white/70 text-sm leading-relaxed">
                      {t('onboarding.step1Hint')}
                    </p>
                  </div>
                </div>
              </>
            )}

            {/* Step 2: Data Check */}
            {step === 2 && (
              <>
                <div className="glass-effect rounded-[15px] p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-white/70 text-sm">{t('onboarding.step2BirthDate')}</span>
                    <span className="text-white font-medium">{birthDateFormatted}</span>
                  </div>
                  <div className="flex items-center justify-between mt-3">
                    <span className="text-white/70 text-sm">{babyName}</span>
                    <span className="text-white font-medium">{ageWeeks} {t('onboarding.step4AgeInfo', { weeks: ageWeeks }).split(':')[0] === t('onboarding.step4AgeInfo', { weeks: ageWeeks }).split(':')[0] ? 'Wochen' : 'weeks'}</span>
                  </div>
                </div>

                {/* Premature birth toggle */}
                <div className="glass-effect rounded-[15px] p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-white text-sm font-medium">{t('onboarding.step2Premature')}</span>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={isPremature}
                      onClick={() => {
                        setIsPremature(!isPremature);
                        if (!isPremature) setPrematureWeeks(0);
                      }}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-pp-orange focus:ring-offset-2 focus:ring-offset-pp-dark ${
                        isPremature ? 'bg-pp-orange' : 'bg-white/20'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${
                          isPremature ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                  {isPremature && (
                    <div className="mt-3">
                      <label className="text-white/70 text-sm">{t('onboarding.step2PrematureWeeks')}</label>
                      <input
                        type="number"
                        min="1"
                        max="16"
                        value={prematureWeeks}
                        onChange={e => setPrematureWeeks(Math.max(0, Math.min(16, Number(e.target.value))))}
                        className="w-full mt-1 p-3 bg-white/10 rounded-[10px] text-white text-center border border-white/20 focus:border-pp-teal focus:outline-none"
                      />
                      <p className="text-white/50 text-xs mt-2">{t('onboarding.step2PrematureHint')}</p>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Step 3: Mode Selection */}
            {step === 3 && (
              <>
                <p className="text-white/80 text-sm leading-relaxed mb-3">
                  {t('onboarding.modeDescription')}
                </p>
                <div className="space-y-3">
                  {/* Observe */}
                  <button
                    onClick={() => setSelectedMode('observe')}
                    className={`w-full text-left p-4 rounded-[15px] transition-all ${
                      selectedMode === 'observe' ? 'bg-pp-teal/20 border border-pp-teal/50' : 'glass-effect'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${selectedMode === 'observe' ? 'bg-pp-teal/20' : 'bg-white/10'}`}>
                        <Eye className={`w-4 h-4 ${selectedMode === 'observe' ? 'text-pp-teal' : 'text-white/40'}`} />
                      </div>
                      <div className="flex-1">
                        <span className="text-white font-medium text-sm block">{t('mode.observe')}</span>
                        <span className="text-white/50 text-xs">{t('onboarding.modeObserveHint')}</span>
                      </div>
                    </div>
                  </button>

                  {/* Learn */}
                  <button
                    onClick={() => canSelectLearn && setSelectedMode('learn')}
                    disabled={!canSelectLearn}
                    className={`w-full text-left p-4 rounded-[15px] transition-all ${
                      !canSelectLearn ? 'glass-effect opacity-50 cursor-not-allowed'
                        : selectedMode === 'learn' ? 'bg-pp-teal/20 border border-pp-teal/50' : 'glass-effect'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${!canSelectLearn ? 'bg-white/10' : selectedMode === 'learn' ? 'bg-pp-teal/20' : 'bg-white/10'}`}>
                        {!canSelectLearn ? (
                          <Lock className="w-4 h-4 text-white/20" />
                        ) : (
                          <BookOpen className={`w-4 h-4 ${selectedMode === 'learn' ? 'text-pp-teal' : 'text-white/40'}`} />
                        )}
                      </div>
                      <div className="flex-1">
                        <span className="text-white font-medium text-sm block">{t('mode.learn')}</span>
                        <span className="text-white/50 text-xs">{canSelectLearn ? t('onboarding.modeLearnHint') : ''}</span>
                      </div>
                      {!canSelectLearn && (
                        <span className="text-white/30 text-xs whitespace-nowrap">{t('onboarding.modeLocked')}</span>
                      )}
                    </div>
                  </button>

                  {/* Active */}
                  <button
                    onClick={() => canSelectActive && setSelectedMode('active')}
                    disabled={!canSelectActive}
                    className={`w-full text-left p-4 rounded-[15px] transition-all ${
                      !canSelectActive ? 'glass-effect opacity-50 cursor-not-allowed'
                        : selectedMode === 'active' ? 'bg-pp-teal/20 border border-pp-teal/50' : 'glass-effect'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${!canSelectActive ? 'bg-white/10' : selectedMode === 'active' ? 'bg-pp-teal/20' : 'bg-white/10'}`}>
                        {!canSelectActive ? (
                          <Lock className="w-4 h-4 text-white/20" />
                        ) : (
                          <Zap className={`w-4 h-4 ${selectedMode === 'active' ? 'text-pp-teal' : 'text-white/40'}`} />
                        )}
                      </div>
                      <div className="flex-1">
                        <span className="text-white font-medium text-sm block">{t('mode.active')}</span>
                        <span className="text-white/50 text-xs">{canSelectActive ? t('onboarding.modeActiveHint') : ''}</span>
                      </div>
                      {!canSelectActive && (
                        <span className="text-white/30 text-xs whitespace-nowrap">{t('onboarding.modeLocked')}</span>
                      )}
                    </div>
                  </button>
                </div>

                {/* Auto-upgrade toggle */}
                <div className="glass-effect rounded-[15px] p-4 mt-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 pr-3">
                      <span className="text-white text-sm font-medium block">{t('onboarding.autoUpgradeTitle')}</span>
                      <span className="text-white/50 text-xs">{t('onboarding.autoUpgradeHint')}</span>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={autoUpgrade}
                      onClick={() => setAutoUpgrade(!autoUpgrade)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-pp-orange focus:ring-offset-2 focus:ring-offset-pp-dark ${
                        autoUpgrade ? 'bg-pp-orange' : 'bg-white/20'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${
                          autoUpgrade ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                </div>
              </>
            )}

            {/* Step 4: Notifications */}
            {step === 4 && (
              <>
                <div className="glass-effect rounded-[15px] p-4 space-y-3">
                  <div className="flex items-center gap-3 mb-2">
                    <Bell className="w-5 h-5 text-pp-teal" />
                    <span className="text-white font-medium">{t('onboarding.step3Title')}</span>
                  </div>
                  <p className="text-white/60 text-sm">
                    {t('onboarding.step3QuietHoursHint')}
                  </p>
                </div>

                {/* Quiet hours */}
                <div className="glass-effect rounded-[15px] p-4">
                  <span className="text-white text-sm font-medium">{t('onboarding.step3QuietHours')}</span>
                  <div className="flex items-center gap-3 mt-2">
                    <div className="flex-1">
                      <label className="text-white/50 text-xs">{t('onboarding.step3From')}</label>
                      <input
                        type="time"
                        defaultValue="22:00"
                        className="w-full p-2 bg-white/10 rounded-[10px] text-white text-center border border-white/20 focus:border-pp-teal focus:outline-none"
                      />
                    </div>
                    <span className="text-white/50 mt-4">-</span>
                    <div className="flex-1">
                      <label className="text-white/50 text-xs">{t('onboarding.step3To')}</label>
                      <input
                        type="time"
                        defaultValue="06:00"
                        className="w-full p-2 bg-white/10 rounded-[10px] text-white text-center border border-white/20 focus:border-pp-teal focus:outline-none"
                      />
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Step 5: Loading Animation → Summary */}
            {step === 5 && (
              <Step5LoadingAnimation
                isDone={summaryReady}
                onDone={() => setSummaryReady(true)}
                isPremature={isPremature}
                prematureWeeks={prematureWeeks}
                correctedAgeWeeks={correctedAgeWeeks}
                ageWeeks={ageWeeks}
                wakeWindow={wakeWindow}
                t={t}
              />
            )}
          </div>

          {/* Footer — hidden during step 5 loading animation */}
          {!(step === 5 && !summaryReady) && (
            <div className="bottom-sheet-footer">
              {step > 1 && (
                <button
                  onClick={() => {
                    if (step === 5) setSummaryReady(false);
                    setStep(step - 1);
                  }}
                  className="flex-1 py-2.5 rounded-xl bg-white/20 border border-white/25 text-white font-medium hover:bg-white/30 flex items-center justify-center gap-1"
                >
                  <ChevronLeft className="w-4 h-4" />
                  {t('onboarding.back')}
                </button>
              )}

              {step < totalSteps ? (
                <button
                  onClick={() => setStep(step + 1)}
                  className="flex-1 py-2.5 rounded-xl bg-pp-teal text-white font-medium hover:bg-pp-teal/90 flex items-center justify-center gap-1"
                >
                  {t('onboarding.next')}
                  <ChevronRight className="w-4 h-4" />
                </button>
              ) : (
                <button
                  onClick={handleActivate}
                  disabled={isActivating}
                  className="flex-1 py-2.5 rounded-xl bg-pp-orange text-white font-medium hover:bg-pp-orange/90 disabled:opacity-50 flex items-center justify-center gap-1"
                >
                  {isActivating ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      {t('onboarding.step4Start')}
                      <ChevronRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </>,
    document.body
  );
}

// ─── Step 5: Loading animation → Summary ────────────────────────────────────

const LOADING_STEPS = [
  { label: 'Alter berechnen …', targetPercent: 25, durationMs: 800 },
  { label: 'Schlafbedarf analysieren …', targetPercent: 50, durationMs: 1500 },
  { label: 'Wachphasen berechnen …', targetPercent: 80, durationMs: 1200 },
  { label: 'Schlafplan erstellen …', targetPercent: 100, durationMs: 1800 },
];

interface Step5Props {
  isDone: boolean;
  onDone: () => void;
  isPremature: boolean;
  prematureWeeks: number;
  correctedAgeWeeks: number;
  ageWeeks: number;
  wakeWindow: { minMinutes: number; maxMinutes: number };
  t: (key: string, opts?: Record<string, unknown>) => string;
}

function Step5LoadingAnimation({
  isDone,
  onDone,
  isPremature,
  prematureWeeks,
  correctedAgeWeeks,
  ageWeeks,
  wakeWindow,
  t,
}: Step5Props) {
  const [activeIndex, setActiveIndex] = useState(-1);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (isDone) return;
    const ctrl = { cancelled: false };

    let currentProgress = 0;

    async function run() {
      for (let i = 0; i < LOADING_STEPS.length; i++) {
        if (ctrl.cancelled) return;
        setActiveIndex(i);

        const step = LOADING_STEPS[i];
        const startP = currentProgress;
        const deltaP = step.targetPercent - startP;
        const frames = Math.ceil(step.durationMs / 16);

        for (let f = 0; f <= frames; f++) {
          if (ctrl.cancelled) return;
          const t = f / frames;
          // Ease-out curve for non-linear filling
          const eased = 1 - Math.pow(1 - t, 2.5);
          currentProgress = startP + deltaP * eased;
          setProgress(currentProgress);
          await new Promise(r => setTimeout(r, 16));
        }

        currentProgress = step.targetPercent;
        setProgress(currentProgress);
      }

      // Brief pause at 100% before showing summary
      await new Promise(r => setTimeout(r, 500));
      if (!ctrl.cancelled) onDone();
    }

    run();
    return () => { ctrl.cancelled = true; };
  }, [isDone, onDone]);

  if (isDone) {
    // Summary view (fades in via CSS)
    return (
      <div className="animate-fadeIn">
        <div className="text-center py-4">
          <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-400" />
          </div>
          <div className="glass-effect rounded-[15px] p-4 text-left space-y-2">
            {isPremature && prematureWeeks > 0 ? (
              <p className="text-white/80 text-sm">
                {t('onboarding.step4CorrectedAge', { weeks: correctedAgeWeeks })}
              </p>
            ) : (
              <p className="text-white/80 text-sm">
                {t('onboarding.step4AgeInfo', { weeks: ageWeeks })}
              </p>
            )}
            <p className="text-pp-teal font-medium">
              {t('onboarding.step4WakeWindow', {
                min: wakeWindow.minMinutes,
                max: wakeWindow.maxMinutes,
              })}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Loading view — text with progress bar below
  const currentLabel = activeIndex >= 0 ? LOADING_STEPS[activeIndex].label : '';

  return (
    <div className="flex flex-col items-center py-8">
      <span
        key={activeIndex}
        className="text-white text-sm font-medium text-center"
        style={{ animation: 'slideUpFadeIn 0.4s ease-out both' }}
      >
        {currentLabel}
      </span>

      <div className="w-full max-w-[240px] h-2 bg-white/10 rounded-full overflow-hidden" style={{ marginTop: 20 }}>
        <div
          className="h-full rounded-full bg-pp-teal transition-none"
          style={{ width: `${progress}%` }}
        />
      </div>

      <style>{`
        @keyframes slideUpFadeIn {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .animate-fadeIn {
          animation: fadeIn 0.5s ease-out both;
        }
      `}</style>
    </div>
  );
}
